import React, { useState } from "react";
import { Button, IconButton } from "ui";
import { PlusIcon } from "icons/outline";
import { toast } from "react-hot-toast";
import ChoiceField from "../../../components/ChoiceField";
import { Conditions } from "./Conditions";
import InputField from "../../../components/InputField";
import Drawer from "../../../components/Drawer";
import { axiosApiInstance } from "../../../pages/api/axios";
import { parseErrorsToArray } from "../../../utils/parseErrors";
import type { RiskRuleFormFields } from "../types";
import { validateConditions, validateFormValues } from "../utils/validation";
import { useRiskManagementConditions } from "../utils/useRiskManagementConditions";
import { useMutation } from "@tanstack/react-query";

type AddNewRuleDrawerProps = {
  refreshTable: () => void;
  isAddEnabled: undefined | boolean;
};

export function AddNewRuleDrawer({ refreshTable, isAddEnabled }: AddNewRuleDrawerProps) {
  const [showCreateRule, setShowCreateRule] = useState(false);

  const [formFields, setFormFields] = useState<RiskRuleFormFields>({});
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof RiskRuleFormFields, string>>
  >({});
  const [conditions, setConditions] = useState<Array<any>>([]);
  const [conditionsError, setConditionsError] = useState<string>("");

  function updateConditions(newConditions: Array<any>) {
    setConditions(newConditions);
  }

  function onFieldChange(fieldName: string, value: string) {
    setFormFields((prev) => ({ ...prev, [fieldName]: value }));
  }

  const addNewRuleMutation = useMutation({
    mutationFn: () =>
      axiosApiInstance.post("/d/risk_management/riskrules/", {
        name: formFields["name"],
        risk_level: formFields["risk_level"],
        reason: formFields["reason"],
        conditions: conditions.map((condition) => {
          return {
            resource_type: condition.resource_type,
            ...condition?.resource_data,
          };
        }),
      }),
    onSuccess: () => {
      toast.success("Rule created successfully");
      setShowCreateRule(false);
      refreshTable();
    },
    onError: (error: any) => {
      const errors = parseErrorsToArray(error);
      toast.error(errors[0]);
    },
  });

  function createRule() {
    setFormErrors({});
    setConditionsError("");

    const formErrors = validateFormValues(formFields);
    if (Object.keys(formErrors ?? {}).length) {
      setFormErrors(formErrors);
      return;
    }

    const conditionsErrors = validateConditions(conditions);
    if (conditionsErrors.errorMessage) {
      setConditionsError(conditionsErrors.errorMessage);
      return;
    }
    addNewRuleMutation.mutate();
  }

  const { data: availableConditions } = useRiskManagementConditions();

  return (
    <>
      {isAddEnabled && <IconButton onClick={() => setShowCreateRule(true)}>
        <PlusIcon className="h-5 w-5" aria-hidden="true" />
      </IconButton>}

      <Drawer
        title="New Risk Rule"
        isOpen={showCreateRule}
        onClose={() => setShowCreateRule(false)}
        size="max-w-lg"
      >
        <div className="h-full overflow-y-auto">
          <div className="m-6 space-y-6">
            <div>
              <InputField
                className="space-y-0"
                field_name="name"
                label="Rule Name"
                value={formFields["name"] ?? ""}
                error_messages={formErrors}
                type="text"
                onChange={onFieldChange}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foundations-bold">
                Rule conditions
              </label>

              <Conditions
                availableConditions={availableConditions}
                conditions={conditions}
                updateConditions={updateConditions}
                error={conditionsError}
              />
            </div>

            <div>
              <ChoiceField
                field_name="risk_level"
                label="Risk Level"
                value={formFields["risk_level"] ?? ""}
                error_messages={formErrors}
                onChange={onFieldChange}
                choices={{
                  MEDIUM_RISK: "Medium",
                  HIGH_RISK: "High",
                }}
              />
            </div>

            <div>
              <InputField
                field_name="reason"
                label={
                  <p>
                    Reason{" "}
                    <span className="text-foundations-subtle">(optional)</span>
                  </p>
                }
                value={formFields["reason"] ?? ""}
                error_messages={formErrors}
                type="text"
                onChange={onFieldChange}
              />
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-t-foundations-subtle px-4 py-5 sm:px-6">
          <div className="flex justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowCreateRule(false)}
              className="ml-3"
            >
              Cancel
            </Button>

            <Button
              type="button"
              isLoading={addNewRuleMutation.isLoading}
              onClick={createRule}
              className="ml-3"
            >
              Create
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
}
